import React, { useState, useEffect } from "react";
import { translate } from "../../../hooks/translate";
import Lozenge from "../../BaseUI/Lozenge";
import { Dropdown, DropdownContent } from "../../BaseUI/Dropdown";
import {
  FiUser,
  FiBriefcase,
  FiActivity,
  FiX,
  FiFilter,
  FiChevronDown,
  FiChevronRight,
  FiCheck,
} from "react-icons/fi";

const DataFilters = ({
  // Data for filter options
  dealers = [],
  companies = [],
  statusOptions = ["active", "inactive", "maintenance"],

  // Current filter values
  selectedDealer = null,
  selectedCompany = null,
  selectedStatus = null,

  // Callbacks for filter changes
  onDealerChange,
  onCompanyChange,
  onStatusChange,
  onClearAll,

  // New callback for applying all filters at once
  onApplyAllFilters,

  // Callback to get companies for a specific dealer
  onGetCompaniesForDealer,

  // Configuration options
  showDealerFilter = true,
  showCompanyFilter = true,
  showStatusFilter = true,

  // Custom labels
  dealerLabel = "Dealer",
  companyLabel = "Company",
  statusLabel = "Status",

  // Custom styling
  className = "",
}) => {
  // State for expanded sections
  const [expandedSections, setExpandedSections] = useState({
    dealer: false,
    company: false,
    status: false,
  });

  // State for pending filter changes
  const [pendingFilters, setPendingFilters] = useState({
    dealer: null,
    company: null,
    status: null,
  });

  // State for filtered companies based on selected dealer
  const [filteredCompanies, setFilteredCompanies] = useState(companies);

  // Initialize pending filters with current selections
  useEffect(() => {
    setPendingFilters({
      dealer: selectedDealer,
      company: selectedCompany,
      status: selectedStatus,
    });
  }, [selectedDealer, selectedCompany, selectedStatus]);

  // Update filtered companies when dealer changes or companies prop changes
  useEffect(() => {
    if (pendingFilters.dealer && onGetCompaniesForDealer) {
      const dealerCompanies = onGetCompaniesForDealer(
        pendingFilters.dealer._id
      );
      setFilteredCompanies(dealerCompanies);

      // Clear company selection if it's not in the filtered list
      if (
        pendingFilters.company &&
        !dealerCompanies.find((c) => c._id === pendingFilters.company._id)
      ) {
        setPendingFilters((prev) => ({ ...prev, company: null }));
      }
    } else {
      setFilteredCompanies(companies);
    }
  }, [pendingFilters.dealer, companies, onGetCompaniesForDealer]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const getStatusAppearance = (status) => {
    switch (status) {
      case "active":
        return "success-subtle";
      case "inactive":
        return "danger-subtle";
      case "maintenance":
        return "warning-subtle";
      case "sold":
        return "info-subtle";
      default:
        return "neutral-subtle";
    }
  };

  const hasActiveFilters = selectedDealer || selectedCompany || selectedStatus;

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedDealer) count++;
    if (selectedCompany) count++;
    if (selectedStatus) count++;
    return count;
  };

  const getFilterSummary = () => {
    const filters = [];
    if (selectedDealer) filters.push(selectedDealer.name);
    if (selectedCompany) filters.push(selectedCompany.name);
    if (selectedStatus)
      filters.push(translate(`UI.${selectedStatus.toUpperCase()}`));

    if (filters.length === 0) return "Filters";
    if (filters.length === 1) return filters[0];
    return `${filters.length} filters`;
  };

  // Check if there are pending changes
  const hasPendingChanges = () => {
    return (
      JSON.stringify(pendingFilters.dealer) !==
        JSON.stringify(selectedDealer) ||
      JSON.stringify(pendingFilters.company) !==
        JSON.stringify(selectedCompany) ||
      pendingFilters.status !== selectedStatus
    );
  };

  // Handle pending filter changes
  const handlePendingDealerChange = (dealer) => {
    setPendingFilters((prev) => ({
      ...prev,
      dealer,
      // Clear company when dealer changes
      company: null,
    }));
  };

  const handlePendingCompanyChange = (company) => {
    setPendingFilters((prev) => ({ ...prev, company }));
  };

  const handlePendingStatusChange = (status) => {
    setPendingFilters((prev) => ({ ...prev, status }));
  };

  // Apply all pending filters
  const applyFilters = () => {
    // If we have the new batch callback, use it for better performance
    if (onApplyAllFilters) {
      onApplyAllFilters(
        pendingFilters.dealer,
        pendingFilters.company,
        pendingFilters.status
      );
    } else {
      // Fallback to individual callbacks
      // Apply filters in a single batch by calling onClearAll first if needed,
      // then applying the new filters
      if (
        !pendingFilters.dealer &&
        !pendingFilters.company &&
        !pendingFilters.status
      ) {
        // If all filters are cleared, use the clear all function
        onClearAll?.();
      } else {
        // Apply filters one by one, but the parent should handle batching
        // We need to call them in the right order to avoid state races

        // First apply dealer filter (this might affect company options)
        if (
          JSON.stringify(pendingFilters.dealer) !==
          JSON.stringify(selectedDealer)
        ) {
          onDealerChange?.(pendingFilters.dealer);
        }

        // Then apply company filter
        if (
          JSON.stringify(pendingFilters.company) !==
          JSON.stringify(selectedCompany)
        ) {
          onCompanyChange?.(pendingFilters.company);
        }

        // Finally apply status filter
        if (pendingFilters.status !== selectedStatus) {
          onStatusChange?.(pendingFilters.status);
        }
      }
    }
  };

  // Clear all pending filters
  const clearAllPendingFilters = () => {
    setPendingFilters({
      dealer: null,
      company: null,
      status: null,
    });
    setFilteredCompanies(companies);
  };

  return (
    <div className={`data-filters-container ${className}`}>
      <div className="filters-wrapper">
        <Dropdown
          placement="bottom-end"
          trigger={
            <button
              className={`filters-btn ${hasActiveFilters ? "active" : ""} ${
                hasPendingChanges() ? "pending" : ""
              }`}
            >
              <FiFilter size={14} />
              <span className="filter-text">{getFilterSummary()}</span>
              {hasActiveFilters && (
                <span className="filter-count">{getActiveFiltersCount()}</span>
              )}
              {hasPendingChanges() && (
                <span className="pending-indicator">•</span>
              )}
              <FiChevronDown size={12} className="chevron" />
            </button>
          }
          customTrigger={true}
          hideDropdownIcon={true}
        >
          <DropdownContent className="unified-dropdown">
            {/* Dealer Filter Section */}
            {showDealerFilter && dealers.length > 0 && (
              <div className="filter-section">
                <div
                  className="filter-section-header clickable"
                  onClick={() => toggleSection("dealer")}
                >
                  <div className="section-title">
                    <FiUser size={14} />
                    <span>{dealerLabel}</span>
                    {pendingFilters.dealer && (
                      <span className="active-indicator">•</span>
                    )}
                  </div>
                  <div className="section-controls">
                    {pendingFilters.dealer && (
                      <button
                        className="section-clear"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePendingDealerChange(null);
                        }}
                      >
                        <FiX size={12} />
                      </button>
                    )}
                    {expandedSections.dealer ? (
                      <FiChevronDown size={12} className="section-chevron" />
                    ) : (
                      <FiChevronRight size={12} className="section-chevron" />
                    )}
                  </div>
                </div>
                {expandedSections.dealer && (
                  <div className="filter-options">
                    <div
                      className={`filter-option ${
                        !pendingFilters.dealer ? "active" : ""
                      }`}
                      onClick={() => handlePendingDealerChange(null)}
                    >
                      Tüm {dealerLabel}ler
                    </div>
                    {dealers.map((dealer) => (
                      <div
                        key={dealer._id}
                        className={`filter-option ${
                          pendingFilters.dealer?._id === dealer._id
                            ? "active"
                            : ""
                        }`}
                        onClick={() => handlePendingDealerChange(dealer)}
                      >
                        {dealer.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Company Filter Section */}
            {showCompanyFilter && (
              <div className="filter-section">
                <div
                  className="filter-section-header clickable"
                  onClick={() => toggleSection("company")}
                >
                  <div className="section-title">
                    <FiBriefcase size={14} />
                    <span>{companyLabel}</span>
                    {pendingFilters.company && (
                      <span className="active-indicator">•</span>
                    )}
                    {pendingFilters.dealer && (
                      <span className="dealer-filter-note">
                        ({pendingFilters.dealer.name})
                      </span>
                    )}
                  </div>
                  <div className="section-controls">
                    {pendingFilters.company && (
                      <button
                        className="section-clear"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePendingCompanyChange(null);
                        }}
                      >
                        <FiX size={12} />
                      </button>
                    )}
                    {expandedSections.company ? (
                      <FiChevronDown size={12} className="section-chevron" />
                    ) : (
                      <FiChevronRight size={12} className="section-chevron" />
                    )}
                  </div>
                </div>
                {expandedSections.company && (
                  <div className="filter-options">
                    <div
                      className={`filter-option ${
                        !pendingFilters.company ? "active" : ""
                      }`}
                      onClick={() => handlePendingCompanyChange(null)}
                    >
                      Tüm {companyLabel}lar
                      {pendingFilters.dealer && (
                        <span className="option-note">
                          (for {pendingFilters.dealer.name})
                        </span>
                      )}
                    </div>
                    {filteredCompanies.length === 0 && pendingFilters.dealer ? (
                      <div className="filter-option disabled">
                        No companies for this dealer
                      </div>
                    ) : (
                      filteredCompanies.map((company) => (
                        <div
                          key={company._id}
                          className={`filter-option ${
                            pendingFilters.company?._id === company._id
                              ? "active"
                              : ""
                          }`}
                          onClick={() => handlePendingCompanyChange(company)}
                        >
                          {company.name}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Status Filter Section */}
            {showStatusFilter && statusOptions.length > 0 && (
              <div className="filter-section">
                <div
                  className="filter-section-header clickable"
                  onClick={() => toggleSection("status")}
                >
                  <div className="section-title">
                    <FiActivity size={14} />
                    <span>{statusLabel}</span>
                    {pendingFilters.status && (
                      <span className="active-indicator">•</span>
                    )}
                  </div>
                  <div className="section-controls">
                    {pendingFilters.status && (
                      <button
                        className="section-clear"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePendingStatusChange(null);
                        }}
                      >
                        <FiX size={12} />
                      </button>
                    )}
                    {expandedSections.status ? (
                      <FiChevronDown size={12} className="section-chevron" />
                    ) : (
                      <FiChevronRight size={12} className="section-chevron" />
                    )}
                  </div>
                </div>
                {expandedSections.status && (
                  <div className="filter-options">
                    <div
                      className={`filter-option ${
                        !pendingFilters.status ? "active" : ""
                      }`}
                      onClick={() => handlePendingStatusChange(null)}
                    >
                      Tüm {statusLabel}lar
                    </div>
                    {statusOptions.map((status) => (
                      <div
                        key={status}
                        className={`filter-option status-option ${
                          pendingFilters.status === status ? "active" : ""
                        }`}
                        onClick={() => handlePendingStatusChange(status)}
                      >
                        <Lozenge
                          appearance={getStatusAppearance(status)}
                          size="small"
                        >
                          {translate(`UI.${status.toUpperCase()}`)}
                        </Lozenge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Apply and Clear Actions */}
            <div className="filter-actions">
              {(pendingFilters.dealer ||
                pendingFilters.company ||
                pendingFilters.status) && (
                <button
                  className="clear-all-pending-btn"
                  onClick={clearAllPendingFilters}
                >
                  <FiX size={14} />
                  Vazgeç
                </button>
              )}

              <button
                className={`apply-filters-btn ${
                  hasPendingChanges() ? "has-changes" : ""
                }`}
                onClick={applyFilters}
                disabled={!hasPendingChanges()}
              >
                <FiCheck size={14} />
                Filtreleri Uygula
              </button>
            </div>

            {/* Clear All Applied Filters (if any exist) */}
            {/* {hasActiveFilters && (
                            <div className="filter-section">
                                <button
                                    className="clear-all-applied-btn"
                                    onClick={() => onClearAll?.()}
                                >
                                    <FiX size={14} />
                                    Clear Applied Filters
                                </button>
                            </div>
                        )} */}
          </DropdownContent>
        </Dropdown>
      </div>

      <style jsx={true}>{`
        .data-filters-container {
          display: flex;
          align-items: center;
        }

        .filters-wrapper {
          display: flex;
          align-items: center;
        }

        .filters-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: #f8f9fa;
          border: 1px solid #e2e5e8;
          border-radius: 8px;
          font-size: 0.875rem;
          color: #495057;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 120px;
          justify-content: space-between;
        }

        .filters-btn:hover {
          background: #e9ecef;
          border-color: #adb5bd;
        }

        .filters-btn.active {
          background: #e3f2fd;
          border-color: #0d6efd;
          color: #0d6efd;
          font-weight: 500;
        }

        .filters-btn.pending {
          background: #fff3cd;
          border-color: #ffc107;
          color: #856404;
        }

        .filter-text {
          flex: 1;
          text-align: left;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 150px;
        }

        .filter-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          font-size: 0.75rem;
          font-weight: 600;
          background-color: #0d6efd;
          color: white;
          border-radius: 10px;
          margin-left: 4px;
        }

        .pending-indicator {
          color: #ffc107;
          font-size: 1.5rem;
          line-height: 1;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        .chevron {
          margin-left: 4px;
          transition: transform 0.2s ease;
        }

        .filters-btn:hover .chevron {
          transform: rotate(180deg);
        }

        .unified-dropdown {
          min-width: 280px;
          max-height: 500px;
          overflow-y: auto;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          border-radius: 12px;
          border: 1px solid #e2e5e8;
          background: white;
          padding: 8px 0;
        }

        .filter-section {
          margin-bottom: 4px;
        }

        .filter-section:last-child {
          margin-bottom: 0;
        }

        .filter-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background-color: #f8f9fa;
          margin: 0 8px;
          border-radius: 6px;
          font-weight: 600;
          font-size: 0.875rem;
          color: #495057;
          transition: all 0.2s ease;
        }

        .filter-section-header.clickable {
          cursor: pointer;
        }

        .filter-section-header.clickable:hover {
          background-color: #e9ecef;
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .dealer-filter-note {
          font-size: 0.75rem;
          color: #6c757d;
          font-weight: 400;
          margin-left: 4px;
        }

        .active-indicator {
          color: #0d6efd;
          font-size: 1.2rem;
          margin-left: 4px;
        }

        .section-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .section-clear {
          background: none;
          border: none;
          color: #6c757d;
          cursor: pointer;
          padding: 2px;
          border-radius: 3px;
          transition: all 0.2s ease;
        }

        .section-clear:hover {
          background: #dc3545;
          color: white;
        }

        .section-chevron {
          transition: transform 0.2s ease;
        }

        .filter-options {
          padding: 4px 8px;
          animation: slideDown 0.2s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .filter-option {
          display: flex;
          align-items: center;
          padding: 8px 12px;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.2s ease;
          font-size: 0.875rem;
          margin: 2px 0;
          margin-left: 20px;
        }

        .filter-option:hover {
          background-color: #f8f9fa;
        }

        .filter-option.active {
          background-color: #e3f2fd;
          color: #0d6efd;
          font-weight: 500;
        }

        .filter-option.disabled {
          color: #6c757d;
          cursor: not-allowed;
          font-style: italic;
        }

        .filter-option.disabled:hover {
          background-color: transparent;
        }

        .option-note {
          font-size: 0.75rem;
          color: #6c757d;
          margin-left: 4px;
        }

        .status-option {
          justify-content: flex-start;
        }

        .filter-actions {
          display: flex;
          position: sticky;
          bottom: 0;
          left: 0;
          right: 0;
          gap: 8px;
          padding: 12px 16px;
          margin: 8px;
          border-top: 1px solid #e9ecef;
          background-color: #f8f9fa;
          border-radius: 6px;
        }

        .clear-all-pending-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          flex: 1;
        }

        .clear-all-pending-btn:hover {
          background: #5a6268;
          transform: translateY(-1px);
        }

        .apply-filters-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          background: #28a745;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          flex: 2;
          position: relative;
        }

        .apply-filters-btn:hover:not(:disabled) {
          background: #218838;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
        }

        .apply-filters-btn.has-changes {
          background: #007bff;
          animation: glow 2s infinite;
        }

        .apply-filters-btn.has-changes:hover {
          background: #0056b3;
        }

        @keyframes glow {
          0%,
          100% {
            box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
          }
          50% {
            box-shadow: 0 0 20px rgba(0, 123, 255, 0.8);
          }
        }

        .apply-filters-btn:disabled {
          background: #6c757d;
          cursor: not-allowed;
          opacity: 0.6;
        }

        .clear-all-applied-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 12px 16px;
          margin: 8px;
          background: #dc3545;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .clear-all-applied-btn:hover {
          background: #c82333;
          transform: translateY(-1px);
        }
      `}</style>
    </div>
  );
};

export default DataFilters;
